import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Box, Container, Typography, Paper, Grid, TextField, Button,
    MenuItem, Alert
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { sendFeedback } from '../../services/GameSlice';

const Contact = () => {
    const dispatch = useDispatch();
    const [form, setForm] = useState({ name: '', email: '', type: 'General Inquiry', message: '' });
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(sendFeedback({
            type: form.type,
            description: `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
        }));
        setSubmitted(true);
        setForm({ name: '', email: '', type: 'General Inquiry', message: '' });
    };

    return (
        <Box>
            <Helmet>
                <title>Contact Us | Play Sequence Online</title>
                <meta name="description" content="Get in touch with the Play Sequence team. Report bugs, request features, or ask questions about our free online Sequence game platform." />
                <meta property="og:title" content="Contact Us - Play Sequence" />
                <meta property="og:description" content="Contact the Play Sequence team for support, feedback, or inquiries about our online Sequence game." />
            </Helmet>

            {/* Hero Section */}
            <Box sx={{ backgroundColor: '#0B2A21', color: '#fff', py: 6, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
                        Contact Us
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        We would love to hear from you
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: 6 }}>
                <Grid container spacing={4}>
                    {/* Contact Form */}
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ p: 4 }}>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1, color: '#16201C' }}>
                                Send Us a Message
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                                Fill out the form below and we will get back to you as soon as possible.
                            </Typography>

                            {submitted && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    Thank you for your message! We will review it and get back to you shortly.
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    label="Your Name"
                                    value={form.name}
                                    onChange={handleChange('name')}
                                    required
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange('email')}
                                    required
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    select
                                    label="Subject"
                                    value={form.type}
                                    onChange={handleChange('type')}
                                    sx={{ mb: 2 }}
                                >
                                    <MenuItem value="General Inquiry">General Inquiry</MenuItem>
                                    <MenuItem value="Bug Report">Bug Report</MenuItem>
                                    <MenuItem value="Feature Request">Feature Request</MenuItem>
                                    <MenuItem value="Privacy Concern">Privacy Concern</MenuItem>
                                </TextField>
                                <TextField
                                    fullWidth
                                    label="Message"
                                    multiline
                                    rows={5}
                                    value={form.message}
                                    onChange={handleChange('message')}
                                    required
                                    sx={{ mb: 3 }}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    sx={{ backgroundColor: '#E2B65B', color: '#16201C', '&:hover': { backgroundColor: '#CDA049' } }}
                                >
                                    Send Message
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Side Info */}
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 4, mb: 3 }}>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2, color: '#16201C' }}>
                                Other Ways to Reach Us
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#3E4A45', lineHeight: 1.8, mb: 2 }}>
                                You can also use the in-game "Feedback" button during any game session to quickly report issues or share suggestions. This sends your feedback directly to our team with relevant game context included.
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#3E4A45', lineHeight: 1.8 }}>
                                For frequently asked questions about gameplay, features, and technical topics, visit our{' '}
                                <Link to="/faq" style={{ color: '#1E6B4F' }}>FAQ page</Link>.
                                You might find your answer there without needing to wait for a reply.
                            </Typography>
                        </Paper>

                        <Paper sx={{ p: 4, mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#16201C' }}>
                                Reporting a Bug?
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#3E4A45', lineHeight: 1.7 }}>
                                When reporting a bug, please include as much detail as possible to help us identify and fix the issue. Helpful information includes the browser and operating system you are using, a description of what you expected to happen versus what actually happened, and the steps to reproduce the issue. Screenshots are also very helpful if you can provide them.
                            </Typography>
                        </Paper>

                        <Paper sx={{ p: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#16201C' }}>
                                Feature Suggestions
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#3E4A45', lineHeight: 1.7 }}>
                                We are always looking for ways to improve Play Sequence. If you have an idea for a new feature or an improvement to an existing one, we would love to hear about it. Please describe the feature, explain how it would improve your experience, and include any examples or references that might help us understand your vision.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Contact;
